import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  Calendar,
  Euro,
  BarChart3,
  UserX,
  MessageCircle,
  UserPlus,
  Check,
  Loader2,
  Crown,
  Lock
} from 'lucide-react';
import { collection, getDocs, query, where, updateDoc, doc } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { db } from '../../config/firebase';
import { useBusiness } from '../../contexts/BusinessContext';
import { useSystemConfig } from '../../hooks/useSystemConfig';

const AnalyticsPage: React.FC = () => {
  const { businessId, currentBusiness } = useBusiness();
  const { config: systemConfig } = useSystemConfig();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<'week' | 'month' | 'year'>('month');
  const [pendingChats, setPendingChats] = useState(0);
  const [pendingApprovals, setPendingApprovals] = useState<Array<{ id: string; name: string; email: string; createdAt: Date }>>([]);
  const [approvingUser, setApprovingUser] = useState<string | null>(null);
  const [analyticsData, setAnalyticsData] = useState({
    users: {
      total: 0,
      newThisMonth: 0,
      growth: 0
    },
    classes: {
      completed: 0,
      cancelled: 0,
      growth: 0
    },
    revenue: {
      total: 0,
      growth: 0
    },
    waitlist: {
      total: 0,
      growth: 0
    }
  });

  // Verificar si el plan actual tiene acceso a estadísticas avanzadas
  const hasStatsAccess = useMemo(() => {
    const currentPlan = (currentBusiness?.subscription?.plan || 'free') as 'free' | 'basic' | 'pro';
    return systemConfig.plans[currentPlan]?.hasAdvancedStats ?? false;
  }, [currentBusiness?.subscription?.plan, systemConfig.plans]);

  // Obtener rango de fechas según el período
  const getDateRange = (periodType: 'week' | 'month' | 'year') => {
    const now = new Date();
    let start: Date;

    if (periodType === 'week') {
      start = new Date(now);
      start.setDate(now.getDate() - 7);
    } else if (periodType === 'month') {
      start = new Date(now.getFullYear(), now.getMonth(), 1);
    } else {
      start = new Date(now.getFullYear(), 0, 1);
    }

    return {
      start: start.toISOString().split('T')[0],
      end: now.toISOString().split('T')[0],
      now
    };
  };

  // Cargar datos reales desde Firestore
  const loadAnalyticsData = async () => {
    if (!businessId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { start, end, now } = getDateRange(period);

      // 1. Obtener usuarios
      const usersQuery = query(collection(db, 'users'), where('businessId', '==', businessId));
      const usersSnapshot = await getDocs(usersQuery);
      const users = usersSnapshot.docs.map(doc => doc.data());
      const totalUsers = users.length;

      // Obtener usuarios nuevos en el período
      const newUsersThisMonth = usersSnapshot.docs.filter(doc => {
        const userData = doc.data();
        let createdAt: string | null = null;
        if (userData.createdAt) {
          if (typeof userData.createdAt === 'string') {
            createdAt = userData.createdAt.split('T')[0];
          } else if (userData.createdAt.toDate) {
            createdAt = userData.createdAt.toDate().toISOString().split('T')[0];
          }
        }
        return createdAt && createdAt >= start && createdAt <= end;
      }).length;

      // 2. Calcular clases realizadas (clases programadas que ya pasaron)
      const classesQuery = query(
        collection(db, 'classes'),
        where('businessId', '==', businessId),
        where('isActive', '==', true)
      );
      const classesSnapshot = await getDocs(classesQuery);

      let completedClasses = 0;
      let waitlistTotal = 0;

      classesSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const classDate = data.date;
        const classTime = data.time || '00:00';

        // Verificar si la clase está en el período seleccionado
        if (classDate >= start && classDate <= end) {
          // Verificar si la clase ya pasó
          const classDateTime = new Date(`${classDate}T${classTime}`);
          if (classDateTime < now) {
            completedClasses++;
          }

          // Contar personas en lista de espera
          const waitlist = data.waitlist || [];
          waitlistTotal += waitlist.length;
        }
      });

      // 3. Calcular ingresos (membresías + bonos del período)
      let totalRevenue = 0;

      // Ingresos de membresías activas
      const userMembershipsQuery = query(
        collection(db, 'userMemberships'),
        where('businessId', '==', businessId),
        where('isActive', '==', true)
      );
      const membershipsSnapshot = await getDocs(userMembershipsQuery);

      // Cargar precios de membresías
      const membershipTypesQuery = query(
        collection(db, 'memberships'),
        where('businessId', '==', businessId)
      );
      const membershipTypesSnapshot = await getDocs(membershipTypesQuery);
      const membershipPrices: { [id: string]: number } = {};
      membershipTypesSnapshot.docs.forEach(doc => {
        const data = doc.data();
        membershipPrices[doc.id] = data.price || 0;
      });

      // Sumar ingresos de membresías asignadas en el período
      membershipsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        // Convertir fecha correctamente (puede ser Timestamp, Date, o string)
        let assignedDate: string | null = null;
        if (data.startDate) {
          if (typeof data.startDate === 'string') {
            assignedDate = data.startDate.split('T')[0];
          } else if (data.startDate.toDate) {
            assignedDate = data.startDate.toDate().toISOString().split('T')[0];
          } else if (data.startDate instanceof Date) {
            assignedDate = data.startDate.toISOString().split('T')[0];
          }
        } else if (data.createdAt?.toDate) {
          assignedDate = data.createdAt.toDate().toISOString().split('T')[0];
        }

        if (assignedDate && assignedDate >= start && assignedDate <= end) {
          const price = membershipPrices[data.membershipId] || 0;
          totalRevenue += price;
        }
      });

      // Cargar precios de bonos
      const passTypesQuery = query(
        collection(db, 'passTypes'),
        where('businessId', '==', businessId)
      );
      const passTypesSnapshot = await getDocs(passTypesQuery);
      const passPrices: { [id: string]: number } = {};
      passTypesSnapshot.docs.forEach(doc => {
        const data = doc.data();
        passPrices[doc.id] = data.price || 0;
      });

      // Sumar ingresos de bonos asignados en el período
      users.forEach(user => {
        if (user.activePass) {
          let purchaseDate: string | null = null;
          if (user.activePass.purchaseDate) {
            if (typeof user.activePass.purchaseDate === 'string') {
              purchaseDate = user.activePass.purchaseDate.split('T')[0];
            }
          }
          if (purchaseDate && purchaseDate >= start && purchaseDate <= end) {
            // Usar el precio del passType o el precio guardado en activePass
            const price = user.activePass.price || passPrices[user.activePass.passTypeId] || 0;
            totalRevenue += price;
          }
        }
      });

      setAnalyticsData({
        users: {
          total: totalUsers,
          newThisMonth: newUsersThisMonth,
          growth: 0
        },
        classes: {
          completed: completedClasses,
          cancelled: 0,
          growth: 0
        },
        revenue: {
          total: totalRevenue,
          growth: 0
        },
        waitlist: {
          total: waitlistTotal,
          growth: 0
        }
      });
    } catch (error) {
      console.error('Error cargando datos de analytics:', error);
      setAnalyticsData({
        users: { total: 0, newThisMonth: 0, growth: 0 },
        classes: { completed: 0, cancelled: 0, growth: 0 },
        revenue: { total: 0, growth: 0 },
        waitlist: { total: 0, growth: 0 }
      });
    } finally {
      setLoading(false);
    }
  };

  // Cargar chats pendientes
  const loadPendingChats = async () => {
    if (!businessId) return;

    try {
      const chatsQuery = query(
        collection(db, 'chats'),
        where('businessId', '==', businessId),
        where('senderRole', '==', 'user'),
        where('isRead', '==', false)
      );
      const chatsSnapshot = await getDocs(chatsQuery);

      // Contar usuarios únicos con mensajes pendientes
      const uniqueUsers = new Set<string>();
      chatsSnapshot.docs.forEach(doc => {
        const senderId = doc.data().senderId;
        if (senderId) uniqueUsers.add(senderId);
      });

      setPendingChats(uniqueUsers.size);
    } catch (error) {
      console.error('Error cargando chats pendientes:', error);
    }
  };

  // Cargar usuarios pendientes de aprobación
  const loadPendingApprovals = async () => {
    if (!businessId) return;

    try {
      const usersQuery = query(
        collection(db, 'users'),
        where('businessId', '==', businessId),
        where('status', '==', 'pending_approval')
      );
      const usersSnapshot = await getDocs(usersQuery);

      const pending = usersSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          name: `${data.firstName} ${data.lastName}`,
          email: data.email,
          createdAt: data.createdAt?.toDate() || new Date()
        };
      });

      setPendingApprovals(pending);
    } catch (error) {
      console.error('Error cargando usuarios pendientes:', error);
    }
  };

  // Aprobar usuario
  const handleApproveUser = async (userId: string) => {
    setApprovingUser(userId);
    try {
      await updateDoc(doc(db, 'users', userId), {
        status: 'active',
        approvedAt: new Date(),
        updatedAt: new Date()
      });
      toast.success('Usuario aprobado correctamente');
      loadPendingApprovals();
    } catch (error) {
      console.error('Error aprobando usuario:', error);
      toast.error('Error al aprobar usuario');
    } finally {
      setApprovingUser(null);
    }
  };

  // Rechazar usuario
  const handleRejectUser = async (userId: string) => {
    if (!confirm('¿Estás seguro de rechazar a este usuario? Se eliminará su cuenta.')) return;

    setApprovingUser(userId);
    try {
      await updateDoc(doc(db, 'users', userId), {
        status: 'rejected',
        rejectedAt: new Date(),
        updatedAt: new Date()
      });
      toast.success('Usuario rechazado');
      loadPendingApprovals();
    } catch (error) {
      console.error('Error rechazando usuario:', error);
      toast.error('Error al rechazar usuario');
    } finally {
      setApprovingUser(null);
    }
  };

  useEffect(() => {
    loadAnalyticsData();
    loadPendingChats();
    loadPendingApprovals();

    // Refrescar cada 30 segundos
    const interval = setInterval(() => {
      loadPendingChats();
      loadPendingApprovals();
    }, 30000);
    return () => clearInterval(interval);
  }, [period, businessId]);

  // Función para formatear números
  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('es-ES').format(num);
  };

  // Función para formatear moneda (EUR)
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  // Función para formatear porcentaje
  const formatPercentage = (value: number) => {
    return `${value > 0 ? '+' : ''}${value.toFixed(1)}%`;
  };

  // Función para obtener el color del crecimiento
  const getGrowthColor = (value: number) => {
    if (value > 0) return 'text-green-600 dark:text-green-400';
    if (value < 0) return 'text-red-600 dark:text-red-400';
    return 'text-gray-600 dark:text-gray-400';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Cargando analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-amber-400">
            Dashboard
          </h1>
          <p className="text-gray-400">
            Estadísticas y métricas de {currentBusiness?.name || 'tu negocio'}
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value as 'week' | 'month' | 'year')}
            className="px-3 py-2 border border-gray-700 rounded-lg bg-gray-800 text-white focus:ring-2 focus:ring-primary-500 text-sm"
          >
            <option value="week">Esta Semana</option>
            <option value="month">Este Mes</option>
            <option value="year">Este Año</option>
          </select>
        </div>
      </div>

      {/* Alerta de Chats Pendientes */}
      {pendingChats > 0 && (
        <Link
          to="/admin/users"
          className="block bg-gradient-to-r from-red-900/50 to-orange-900/50 border border-red-700 rounded-2xl p-4 hover:from-red-900/70 hover:to-orange-900/70 transition-all"
        >
          <div className="flex items-center gap-4">
            <div className="relative">
              <div className="w-14 h-14 bg-red-600 rounded-xl flex items-center justify-center">
                <MessageCircle className="w-7 h-7 text-white" />
              </div>
              <span className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold animate-pulse">
                {pendingChats}
              </span>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-white">
                {pendingChats === 1 ? 'Tienes 1 chat pendiente' : `Tienes ${pendingChats} chats pendientes`}
              </h3>
              <p className="text-red-300 text-sm">
                Hay usuarios esperando tu respuesta. Haz clic para ver los mensajes.
              </p>
            </div>
            <div className="text-red-400">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>
        </Link>
      )}

      {/* Alerta de Usuarios Pendientes de Aprobación */}
      {pendingApprovals.length > 0 && (
        <div className="bg-gradient-to-r from-amber-900/50 to-yellow-900/50 border border-amber-700 rounded-2xl p-4">
          <div className="flex items-center gap-4 mb-4">
            <div className="relative">
              <div className="w-14 h-14 bg-amber-600 rounded-xl flex items-center justify-center">
                <UserPlus className="w-7 h-7 text-white" />
              </div>
              <span className="absolute -top-2 -right-2 w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center text-white text-xs font-bold animate-pulse">
                {pendingApprovals.length}
              </span>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-white">
                {pendingApprovals.length === 1 ? '1 usuario pendiente de aprobación' : `${pendingApprovals.length} usuarios pendientes de aprobación`}
              </h3>
              <p className="text-amber-300 text-sm">
                Nuevos usuarios esperando tu aprobación para acceder al sistema.
              </p>
            </div>
          </div>

          {/* Lista de usuarios pendientes */}
          <div className="space-y-2">
            {pendingApprovals.map(user => (
              <div key={user.id} className="flex items-center justify-between bg-gray-900/50 rounded-lg p-3">
                <div>
                  <p className="text-white font-medium">{user.name}</p>
                  <p className="text-gray-400 text-sm">{user.email}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleRejectUser(user.id)}
                    disabled={approvingUser === user.id}
                    className="px-3 py-1.5 bg-red-600/20 text-red-400 rounded-lg text-sm hover:bg-red-600/40 transition-colors disabled:opacity-50"
                  >
                    Rechazar
                  </button>
                  <button
                    onClick={() => handleApproveUser(user.id)}
                    disabled={approvingUser === user.id}
                    className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-1"
                  >
                    {approvingUser === user.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Check className="w-4 h-4" />
                    )}
                    Aprobar
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* KPI Cards - Con overlay de paywall si no hay acceso */}
      <div className="relative">
        {/* Overlay de Paywall */}
        {!hasStatsAccess && (
          <div className="absolute inset-0 z-10 flex items-center justify-center">
            {/* Fondo con blur */}
            <div className="absolute inset-0 bg-gray-900/80 backdrop-blur-sm rounded-2xl"></div>

            {/* Contenido del Paywall */}
            <div className="relative z-20 text-center p-8 max-w-lg">
              <div className="w-20 h-20 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-xl shadow-amber-500/30">
                <Crown className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-3">
                Estadísticas Premium
              </h2>
              <p className="text-gray-300 mb-6">
                Accede a estadísticas avanzadas de tu negocio: usuarios, clases, ingresos y más.
                Contrata el <span className="text-amber-400 font-semibold">Plan Pro</span> para desbloquear todas las métricas.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link
                  to="/admin/config"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-black font-semibold rounded-xl transition-all transform hover:scale-105 shadow-lg shadow-amber-500/30"
                >
                  <Crown className="w-5 h-5" />
                  Contratar Plan Pro
                </Link>
              </div>
              <p className="text-gray-500 text-sm mt-4">
                <Lock className="w-4 h-4 inline mr-1" />
                Incluye estadísticas ilimitadas, soporte prioritario y más
              </p>
            </div>
          </div>
        )}

        {/* Contenido de estadísticas (se ve borroso si no hay acceso) */}
        <div className={!hasStatsAccess ? 'filter blur-sm pointer-events-none select-none' : ''}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Usuarios */}
            <div className="bg-gray-900 rounded-2xl border border-gray-800 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-400">
                    Total Usuarios
                  </p>
                  <p className="text-3xl font-bold text-white">
                    {formatNumber(analyticsData.users.total)}
                  </p>
                  <p className="text-sm text-gray-500">
                    {analyticsData.users.newThisMonth} nuevos este mes
                  </p>
                  <p className={`text-sm font-medium ${getGrowthColor(analyticsData.users.growth)}`}>
                    {analyticsData.users.growth !== 0 ? formatPercentage(analyticsData.users.growth) : 'Sin datos previos'} vs mes anterior
                  </p>
                </div>
                <div className="p-3 bg-blue-900/30 rounded-xl">
                  <Users className="h-8 w-8 text-blue-400" />
                </div>
              </div>
            </div>

            {/* Clases */}
            <div className="bg-gray-900 rounded-2xl border border-gray-800 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-400">
                    Clases Realizadas
                  </p>
                  <p className="text-3xl font-bold text-white">
                    {formatNumber(analyticsData.classes.completed)}
                  </p>
                  <p className="text-sm text-gray-500">
                    {analyticsData.classes.cancelled} canceladas
                  </p>
                  <p className={`text-sm font-medium ${getGrowthColor(analyticsData.classes.growth)}`}>
                    {analyticsData.classes.growth !== 0 ? formatPercentage(analyticsData.classes.growth) : 'Sin datos previos'} vs mes anterior
                  </p>
                </div>
                <div className="p-3 bg-green-900/30 rounded-xl">
                  <Calendar className="h-8 w-8 text-green-400" />
                </div>
              </div>
            </div>

            {/* Ingresos */}
            <div className="bg-gray-900 rounded-2xl border border-gray-800 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-400">
                    Ingresos del Período
                  </p>
                  <p className="text-3xl font-bold text-white">
                    {formatCurrency(analyticsData.revenue.total)}
                  </p>
                  <p className="text-sm text-gray-500">
                    Membresías + Bonos
                  </p>
                  <p className={`text-sm font-medium ${getGrowthColor(analyticsData.revenue.growth)}`}>
                    {analyticsData.revenue.growth !== 0 ? formatPercentage(analyticsData.revenue.growth) : 'Sin datos previos'} vs período anterior
                  </p>
                </div>
                <div className="p-3 bg-amber-900/30 rounded-xl">
                  <Euro className="h-8 w-8 text-amber-400" />
                </div>
              </div>
            </div>

            {/* Lista de Espera */}
            <div className="bg-gray-900 rounded-2xl border border-gray-800 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-400">
                    Lista de Espera
                  </p>
                  <p className="text-3xl font-bold text-white">
                    {formatNumber(analyticsData.waitlist.total)}
                  </p>
                  <p className="text-sm text-gray-500">
                    Personas sin plaza
                  </p>
                  <p className={`text-sm font-medium ${getGrowthColor(analyticsData.waitlist.growth)}`}>
                    {analyticsData.waitlist.growth !== 0 ? formatPercentage(analyticsData.waitlist.growth) : 'Sin datos previos'} vs período anterior
                  </p>
                </div>
                <div className="p-3 bg-purple-900/30 rounded-xl">
                  <UserX className="h-8 w-8 text-purple-400" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mensaje informativo cuando no hay datos */}
      {analyticsData.users.total === 0 && analyticsData.classes.completed === 0 && (
        <div className="bg-gray-900 rounded-2xl border border-gray-800 p-8 text-center">
          <BarChart3 className="h-16 w-16 text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">
            No hay datos disponibles
          </h3>
          <p className="text-gray-400 mb-4">
            Los analytics se mostrarán cuando comiences a registrar usuarios, clases y pagos en el sistema.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-500">
            <div className="p-3 bg-gray-800 rounded-lg">
              <strong className="text-gray-300">Usuarios:</strong> Registra miembros en Gestión de Usuarios
            </div>
            <div className="p-3 bg-gray-800 rounded-lg">
              <strong className="text-gray-300">Clases:</strong> Crea clases en Gestión de Calendario
            </div>
            <div className="p-3 bg-gray-800 rounded-lg">
              <strong className="text-gray-300">Pagos:</strong> Gestiona cuotas en Gestión de Usuarios
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsPage; 